import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { SiteLink } from "./SiteLink";
import { mainHref } from "../lib/siteUrls";
import { isProd, isDebug, readConsent, saveConsent } from "../lib/analytics";

function Toggle({ id, label, hint, checked, onChange }) {
  return (
    <label htmlFor={id} className="flex items-start justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4 cursor-pointer">
      <span>
        <span className="block text-sm font-semibold text-white">{label}</span>
        <span className="block text-xs text-neutral-400 mt-1 leading-relaxed">{hint}</span>
      </span>
      <Switch id={id} checked={checked} onCheckedChange={onChange} data-testid={`${id}-switch`} className="data-[state=checked]:bg-[#FFDA7C]" />
    </label>
  );
}

/* Bottom consent bar (production only, or ?analytics_debug=1 in preview). Reopened by the footer "Cookie settings" link. */
export function CookieConsent() {
  const { pathname } = useLocation();
  const enabled = isProd() || isDebug();
  const [open, setOpen] = useState(() => enabled && !readConsent());
  const [manage, setManage] = useState(false);
  const saved = readConsent();
  const [analytics, setAnalytics] = useState(saved?.analytics ?? true);
  const [marketing, setMarketing] = useState(saved ? !!saved.marketing : true);

  useEffect(() => {
    const reopen = () => { const c = readConsent(); if (c) { setAnalytics(c.analytics ?? true); setMarketing(!!c.marketing); } setManage(true); setOpen(true); };
    window.addEventListener("stl:open-cookie-settings", reopen);
    return () => window.removeEventListener("stl:open-cookie-settings", reopen);
  }, []);

  if (!enabled || !open || pathname === "/feedback2026" || pathname.startsWith("/feedback2026/")) return null;
  const choose = (c) => { saveConsent(c); setOpen(false); setManage(false); };

  return (
    <div role="dialog" aria-live="polite" aria-label="Cookie preferences" className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-5 animate-in slide-in-from-bottom-4 fade-in duration-300" data-testid="cookie-consent">
      <div className="mx-auto max-w-5xl rounded-3xl bg-[#141414]/95 backdrop-blur-xl border border-white/10 shadow-2xl text-white p-5 sm:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-5">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <span className="mt-0.5 w-9 h-9 shrink-0 rounded-full grid place-items-center" style={{ background: "rgba(255,218,124,0.15)", color: "#FFDA7C" }}><Cookie className="w-4 h-4" /></span>
            <p className="text-sm text-neutral-300 leading-relaxed">
              We use cookies to understand how our site is used and to measure our outreach. You choose what&apos;s allowed.{" "}
              <SiteLink href={mainHref("/privacy-policy")} className="underline underline-offset-2 text-white hover:text-[#FFDA7C] transition-colors" data-testid="cookie-privacy-link">Privacy policy</SiteLink>
            </p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Button variant="ghost" onClick={() => setManage((m) => !m)} className="rounded-full h-10 px-5 text-white hover:bg-white/10 hover:text-white" data-testid="cookie-manage-btn">Manage</Button>
            <Button variant="outline" onClick={() => choose({ analytics: false, marketing: false })} className="rounded-full h-10 px-5 bg-transparent border-white/25 text-white hover:bg-white/10 hover:text-white" data-testid="cookie-reject-btn">Reject</Button>
            <Button onClick={() => choose({ analytics: true, marketing: true })} className="rounded-full h-10 px-6 font-semibold text-black hover:opacity-90" style={{ background: "#FFDA7C" }} data-testid="cookie-accept-btn">Accept all</Button>
          </div>
        </div>
        {manage && (
          <div className="mt-5 pt-5 border-t border-white/10 grid sm:grid-cols-2 gap-3" data-testid="cookie-manage-panel">
            <Toggle id="cookie-analytics" label="Analytics" hint="Helps us see which pages are useful, so we can improve them." checked={analytics} onChange={setAnalytics} />
            <Toggle id="cookie-marketing" label="Marketing" hint="Measures our ads and lets partners show relevant messages." checked={marketing} onChange={setMarketing} />
            <div className="sm:col-span-2 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setManage(false)} className="rounded-full h-10 px-4 text-neutral-300 hover:bg-white/10 hover:text-white" aria-label="Close settings" data-testid="cookie-manage-close"><X className="w-4 h-4" /></Button>
              <Button onClick={() => choose({ analytics, marketing })} className="rounded-full h-10 px-6 font-semibold bg-white text-black hover:bg-neutral-200" data-testid="cookie-save-btn">Save choices</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
